import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Config } from '@core/models/config.model';
import { AppError } from '@core/models/error.model';
import { LifePlan, LifePlanStatus } from '@core/models/life-plan.model';
import { LifePlanManagerService } from '@core/services/life-plan-manager.service';
import { LoginService } from '@core/services/login.service';
import { ReportsService } from '@core/services/reports.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

type LifePlanGuardErrorType = 'UNKNOWN' | 'LIFE_PLAN_BAD_STATUS';

class LifePlanGuardError extends Error {
  constructor(
    m: string,
    public type: LifePlanGuardErrorType,
    public args: any
  ) {
    super(m);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, LifePlanGuardError.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class LifePlanGuard implements CanActivate {
  config!: Config;
  currentLifePlan?: LifePlan;
  defaultLifePlan?: LifePlan;

  constructor(
    private lifePlanManager: LifePlanManagerService,
    private router: Router,
    private store: Store<AppState>,
    private reportsService: ReportsService,
    private uiService: UiService,
    private loginService: LoginService
  ) {
    this.store.subscribe({
      next: (state) => {
        this.config = state.config;
        this.currentLifePlan = state.lifePlan.currentLifePlan;
        this.defaultLifePlan = state.lifePlan.defaultLifePlan;
      },
    });
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const urlPrefix = route.routeConfig?.path
      ?.split('/')
      .filter((element) => element !== '')[0];
    const lifePlanId = route.paramMap.get('lifePlanId');
    if (!lifePlanId) {
      return false;
    }
    if (
      lifePlanId === this.currentLifePlan?.id &&
      this.isLifePlanStatusAllowed(this.currentLifePlan.status) &&
      this.currentLifePlan.insurances &&
      this.defaultLifePlan?.insurances
    ) {
      // Do not perform API calls if the store has the current life plan and insurances are completed
      // also for the defaultLifePlan
      return true;
    }
    this.uiService.presentLoading();
    return this.lifePlanManager.getAndManageLifePlan(lifePlanId).pipe(
      switchMap((lifePlan) => {
        this.uiService.presentLoading();
        if (lifePlan && !this.isLifePlanStatusAllowed(lifePlan.status)) {
          throw new LifePlanGuardError(
            `Status is not allowed. Life plan status: ${lifePlan.status}`,
            'LIFE_PLAN_BAD_STATUS',
            {
              lifePlanStatus: lifePlan.status,
            }
          );
        }
        if (!lifePlan.insurances) {
          return this.lifePlanManager
            .generateLifePlanInsuranceTargetsAndPopulateThemWithProducts(
              lifePlan
            )
            .pipe(
              switchMap((insuranceGeneratedLifePlan) =>
                this.lifePlanManager.updateAndManageLifePlan(
                  insuranceGeneratedLifePlan
                )
              ),
              tap((newLifePlan) =>
                this.store.dispatch(
                  actions.forceUpdateDefaultLifePlan({
                    defaultLifePlan: newLifePlan,
                  })
                )
              ),
              // When can not generate targets, return to DRAFT status
              catchError((err) => {
                const lifePlanReseted = JSON.parse(JSON.stringify(lifePlan));
                lifePlanReseted.status = 'DRAFT';
                return this.lifePlanManager
                  .updateAndManageLifePlan(lifePlanReseted)
                  .pipe(
                    tap((lp) => {
                      const error = Error(err!);
                      this.uiService.openErrorModal(error);
                      throw new LifePlanGuardError(
                        `Status is not allowed. Life plan status: ${lifePlanReseted.status}`,
                        'LIFE_PLAN_BAD_STATUS',
                        {
                          lifePlanStatus: lifePlanReseted.status,
                        }
                      );
                    })
                  );
              })
            );
        }
        // Check for the default life plan and generate its insurances from the retrieved lifePlan
        // but forcing the recommended products
        if (!this.defaultLifePlan?.insurances) {
          return this.lifePlanManager
            .generateLifePlanInsuranceTargetsAndPopulateThemWithProducts(
              lifePlan,
              'all'
            )
            .pipe(
              map((insuranceGeneratedLifePlan) => {
                this.store.dispatch(
                  actions.forceUpdateDefaultLifePlan({
                    defaultLifePlan: insuranceGeneratedLifePlan,
                  })
                );
                return lifePlan;
              })
            );
        }
        return of(lifePlan);
      }),
      switchMap((lifePlan) =>
        this.reportsService.getAndUpdateReportsData(lifePlan)
      ),
      map((reportsData) => !!reportsData),
      tap((allOk) => this.uiService.dismissLoading()), // No errors if this is reached
      catchError((err) => {
        console.error(err);
        this.uiService.dismissLoading();
        this.store.dispatch(actions.cleanLifePlanState());
        if (err.type === 'LIFE_PLAN_BAD_STATUS') {
          if (err.args?.lifePlanStatus === 'DRAFT') {
            let draftUrl = '/home';
            if (urlPrefix === 'agent') {
              draftUrl = `/agent/life-plans/${lifePlanId}/form`;
            }
            this.router.navigateByUrl(draftUrl);
            return of(false);
          }
        }
        // The above redirect is not considered an error
        //this.uiService.openErrorModal();
        let url = '/home';
        if (urlPrefix === 'agent') {
          url = '/agent/life-plans';
        } else {
          this.loginService.onLogout();
        }
        const tree: UrlTree = this.router.parseUrl(url);
        return of(tree);
      })
    );
  }

  private isLifePlanStatusAllowed(status: LifePlanStatus): boolean {
    return status === 'NORMAL' || status === 'SENT';
  }
}
